import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const MAX_HISTORY_MS = 1 * 60 * 60 * 1000 // 1 hour

const CONFIG_COLUMNS =
	'sensor_location, show_alkalinity, show_ph, show_temperature, show_conductivity'

const MEASUREMENT_COLUMNS =
	'timestamp, total_alkalinity_ppm_rolling_avg, ph, temperature_c, conductivity_mscm'

export function useRealtime(customerId, sensorId) {
	const [history, setHistory] = useState({
		alkalinity: [],
		temperature: [],
		conductivity: [],
		ph: []
	})
	const [sensorLocation, setSensorLocation] = useState(null)
	const [availableFields, setAvailableFields] = useState({
		alkalinity: false,
		ph: false,
		temperature: false,
		conductivity: false
	})
	const [status, setStatus] = useState('disconnected')

	const processHistoricalData = useCallback((measurements) => {
		const newHistory = {
			alkalinity: [],
			temperature: [],
			conductivity: [],
			ph: []
		}

		const cutoff = Date.now() - MAX_HISTORY_MS

		measurements.forEach(m => {
			const timestamp = new Date(m.timestamp).getTime()
			if (timestamp < cutoff) return

			if (m.total_alkalinity_ppm_rolling_avg != null) {
				newHistory.alkalinity.push({
					value: m.total_alkalinity_ppm_rolling_avg,
					timestamp: m.timestamp
				})
			}
			if (m.ph != null) {
				newHistory.ph.push({
					value: m.ph,
					timestamp: m.timestamp
				})
			}
			if (m.temperature_c != null) {
				newHistory.temperature.push({
					value: m.temperature_c,
					timestamp: m.timestamp
				})
			}
			if (m.conductivity_mscm != null) {
				newHistory.conductivity.push({
					value: m.conductivity_mscm,
					timestamp: m.timestamp
				})
			}
		})

		setHistory(newHistory)
	}, [])

	const processNewMeasurement = useCallback((payload) => {
		const timestamp = new Date(payload.timestamp).getTime()
		const cutoff = Date.now() - MAX_HISTORY_MS

		if (timestamp < cutoff) return

		setHistory(prev => {
			// Deduplicate: both broadcast and CDC may fire for the same row
			const isDuplicate = (arr) => arr.some(p => p.timestamp === payload.timestamp)

			const newHistory = { ...prev }

			if (payload.total_alkalinity_ppm_rolling_avg != null && !isDuplicate(prev.alkalinity)) {
				newHistory.alkalinity = [...prev.alkalinity, {
					value: payload.total_alkalinity_ppm_rolling_avg,
					timestamp: payload.timestamp
				}].filter(p => new Date(p.timestamp).getTime() > cutoff)
			}
			if (payload.ph != null && !isDuplicate(prev.ph)) {
				newHistory.ph = [...prev.ph, {
					value: payload.ph,
					timestamp: payload.timestamp
				}].filter(p => new Date(p.timestamp).getTime() > cutoff)
			}
			if (payload.temperature_c != null && !isDuplicate(prev.temperature)) {
				newHistory.temperature = [...prev.temperature, {
					value: payload.temperature_c,
					timestamp: payload.timestamp
				}].filter(p => new Date(p.timestamp).getTime() > cutoff)
			}
			if (payload.conductivity_mscm != null && !isDuplicate(prev.conductivity)) {
				newHistory.conductivity = [...prev.conductivity, {
					value: payload.conductivity_mscm,
					timestamp: payload.timestamp
				}].filter(p => new Date(p.timestamp).getTime() > cutoff)
			}

			return newHistory
		})
	}, [])

	const fetchInitialData = useCallback(async () => {
		if (!customerId || !sensorId) return

		setStatus('connecting')

		// Clear previous sensor's data
		setHistory({
			alkalinity: [],
			temperature: [],
			conductivity: [],
			ph: []
		})
		setSensorLocation(null)

		try {
			const oneHourAgo = new Date(Date.now() - MAX_HISTORY_MS).toISOString()

			const [configResult, measurementsResult] = await Promise.all([
				supabase
					.from('sensor_configs')
					.select(CONFIG_COLUMNS)
					.eq('customer_id', customerId)
					.eq('sensor_id', sensorId)
					.eq('delivery_enabled', true)
					.eq('realtime_enabled', true)
					.single(),
				supabase
					.from('aggregated_measurements')
					.select(MEASUREMENT_COLUMNS)
					.eq('customer_id', customerId)
					.eq('sensor_id', sensorId)
					.gte('timestamp', oneHourAgo)
					.order('timestamp', { ascending: true }),
			])

			const configData = configResult.data
			if (!configData) {
				setStatus('error')
				return
			}

			setAvailableFields({
				alkalinity: configData.show_alkalinity,
				ph: configData.show_ph,
				temperature: configData.show_temperature,
				conductivity: configData.show_conductivity
			})
			setSensorLocation(configData.sensor_location)

			if (measurementsResult.data) {
				processHistoricalData(measurementsResult.data)
			}

			setStatus('connected')
		} catch (err) {
			console.error('Error fetching initial data:', err)
			setStatus('error')
		}
	}, [customerId, sensorId, processHistoricalData])

	useEffect(() => {
		if (!customerId || !sensorId) return

		fetchInitialData()

		// Primary path: broadcast from ingest-data edge function
		const broadcastChannel = supabase
			.channel(`measurement:${customerId}:${sensorId}`)
			.on('broadcast', { event: 'measurement' }, (payload) => {
				const data = payload.payload
				if (!data) return
				// The broadcast payload uses total_alkalinity_ppm; remap to
				// the column name processNewMeasurement expects.
				processNewMeasurement({
					total_alkalinity_ppm_rolling_avg: data.total_alkalinity_ppm,
					ph: data.ph,
					temperature_c: data.temperature_c,
					conductivity_mscm: data.conductivity_mscm,
					timestamp: data.timestamp,
				})
			})
			.subscribe((status) => {
				if (status === 'SUBSCRIBED') {
					console.debug('[use-realtime] Broadcast channel subscribed', { customerId, sensorId })
				} else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
					console.warn('[use-realtime] Broadcast channel error:', status)
				}
			})

		// CDC fallback with server-side filters to avoid fan-out of all inserts
		const cdcChannel = supabase
			.channel(`cdc:measurements:${customerId}:${sensorId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'aggregated_measurements',
					filter: `customer_id=eq.${customerId}`,
				},
				(payload) => {
					if (payload.new.sensor_id !== sensorId) return
					processNewMeasurement(payload.new)
				}
			)
			.subscribe((status) => {
				if (status === 'SUBSCRIBED') {
					console.debug('[use-realtime] CDC channel subscribed', { customerId, sensorId })
				} else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
					// CDC failures are expected when the Realtime publication
					// doesn't include aggregated_measurements. The broadcast
					// channel is the primary path, so don't mark status as
					// error here — just log it.
					console.warn('[use-realtime] CDC channel error (non-fatal, broadcast is primary):', status)
				}
			})

		return () => {
			supabase.removeChannel(broadcastChannel)
			supabase.removeChannel(cdcChannel)
		}
	}, [customerId, sensorId, fetchInitialData, processNewMeasurement])

	return {
		history,
		sensorLocation,
		availableFields,
		status
	}
}
